import { Link } from 'react-router-dom';
import {
  ACCESS_PATHS,
  FACTS,
  HERO,
  HOME_COMING_SOON,
  HOME_CTA,
  HOME_HIGHLIGHTS,
  HOME_SOLUTIONS,
  PLANES,
  PLANE_TRADEOFF,
} from '@/content/home';
import { COMING_SOON_CATEGORY_ID, productsInCategory } from '@/content/products';
import { ActionRow, TextLink } from '@/components/ActionButton';
import { Card, CheckList } from '@/components/Card';
import { Icon } from '@/components/Icon';
import { ProductCard } from '@/components/ProductCard';
import { Reveal, Stat, StatGrid } from '@/components/Reveal';
import { Screenshot } from '@/components/Screenshot';
import { Section, SectionHeading } from '@/components/Section';
import { Seo } from '@/components/Seo';
import { useUpcomingProducts } from '@/hooks/useUpcomingProducts';
import { SHOT_SIZES_ASIDE, SHOT_SIZES_FULL } from '@/content/shots';
import { ArchitectureDiagram } from '@/illustrations/ArchitectureDiagram';
import { FleetOverviewMock, SingleDroneMock } from '@/illustrations/PlatformUI';

/**
 * 首页。
 *
 * 分区顺序对齐阿里云官网：渐变 Hero → 四宫格卖点 → 硬指标 → 产品体系 → 架构取舍
 * → 界面一览 → 即将上线 → 接入方式 → 适用场景 → 收口 CTA。
 * 区块之间靠白面与近白底交替拉开层次，不再用大面积的灰蓝填充。
 */
export function HomePage() {
  const modules = productsInCategory('platform');
  // 后台维护的待上新产品优先；接口不可用时回落到静态内容，首页不会整块消失。
  const remoteComingSoon = useUpcomingProducts();
  const comingSoon =
    remoteComingSoon && remoteComingSoon.length > 0
      ? remoteComingSoon
      : productsInCategory(COMING_SOON_CATEGORY_ID);

  return (
    <>
      <Seo path="/" crumbs={[{ name: '首页', path: '/' }]} />

      <Section tone="hero" spacing="loose" className="border-b border-line">
        <div className="grid grid-cols-[minmax(0,1fr)] gap-12 lg:grid-cols-[minmax(0,1fr)_27rem] lg:items-start">
          <div className="flex flex-col gap-6">
            <span className="label-mono text-brand">{HERO.eyebrow}</span>
            <h1 className="max-w-2xl text-[1.875rem] leading-[1.15] font-semibold text-fg md:text-[3.25rem] md:leading-[1.12]">
              {HERO.titleLead}
              <span className="text-brand">{HERO.titleHighlight}</span>
            </h1>
            <p className="max-w-2xl text-base leading-relaxed text-fg-muted md:text-[1.0625rem]">{HERO.lead}</p>
            <ActionRow actions={HERO.actions} />
            <p className="flex max-w-xl items-start gap-2.5 rounded-xl border border-line bg-surface-1 p-3.5 text-xs leading-relaxed text-fg-muted">
              <Icon name="cpu" size={15} className="mt-0.5 shrink-0 text-brand" />
              {HERO.note}
            </p>
          </div>

          <Reveal delay={1} className="flex flex-col gap-4">
            <FleetOverviewMock />
            <SingleDroneMock />
          </Reveal>
        </div>
      </Section>

      <Section tone="raised" spacing="tight">
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {HOME_HIGHLIGHTS.map((item, index) => (
            <Reveal key={item.title} delay={index}>
              <Link to={item.to} className="group flex flex-col gap-2.5">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-grad-tint text-brand">
                  <Icon name={item.icon} size={18} />
                </span>
                <h2 className="text-[0.9375rem] font-semibold text-fg transition-colors group-hover:text-brand">
                  {item.title}
                </h2>
                <p className="text-[0.8125rem] leading-relaxed text-fg-muted">{item.text}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section tone="soft" spacing="tight">
        <StatGrid columns={4} className="gap-y-10">
          {FACTS.map((fact, index) => (
            <Reveal key={fact.label} delay={index}>
              <Stat value={fact.value} unit={fact.unit} label={fact.label} hint={fact.detail} />
            </Reveal>
          ))}
        </StatGrid>
      </Section>

      <Section id="products">
        <SectionHeading
          eyebrow="产品体系"
          title="六个模块，构成从建档到追溯的完整链路"
          lead="每个模块只解决一件事，模块之间通过数据衔接，无需人工搬运。围绕它们还有实时链路、阈值定义与开放接入。"
          actions={<TextLink to="/products">查看全部产品</TextLink>}
        />

        <div className="mt-10 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {modules.map((product, index) => (
            <Reveal key={product.id} delay={index} as="div">
              <ProductCard product={product} className="h-full" />
            </Reveal>
          ))}
        </div>
      </Section>

      <Section tone="raised" id="architecture">
        <SectionHeading
          eyebrow="架构取舍"
          title="控制面与数据面分离，遥测不经过平台"
          lead="平台仅负责账号、权限、设备档案与令牌；高频遥测由浏览器直接向机体获取。这是经过权衡的设计，收益与代价一并列出。"
        />

        <div className="mt-10 grid grid-cols-[minmax(0,1fr)] gap-6 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start">
          <Reveal>
            <ArchitectureDiagram />
          </Reveal>

          <div className="flex flex-col gap-4">
            {PLANES.map((plane) => (
              <Card key={plane.id}>
                <div className="flex items-center gap-2.5">
                  <Icon name={plane.icon} size={18} className="text-brand" />
                  <span className="label-mono">{plane.role}</span>
                </div>
                <h3 className="mt-3 text-base font-semibold text-fg">{plane.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-fg-muted">{plane.subtitle}</p>
                <div className="mt-4 border-t border-line pt-4">
                  <CheckList items={plane.responsibilities} />
                </div>
              </Card>
            ))}
          </div>
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {PLANE_TRADEOFF.map((item) => (
            <div key={item.label} className="rounded-xl border border-line bg-surface-1 p-4">
              <span className="label-mono">{item.label}</span>
              <p className="mt-2 text-sm leading-relaxed text-fg-muted">{item.text}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section id="screens">
        <SectionHeading
          eyebrow="界面一览"
          title="界面实际呈现"
          lead="以下两张为演示环境中的真实截图，并非设计稿。先看机队总览如何统管全局，再看单机管理页如何盯住单台机体的每项指标。"
        />

        <div className="mt-10 grid grid-cols-[minmax(0,1fr)] gap-8 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start">
          <div className="flex flex-col gap-4">
            <p className="text-sm leading-relaxed text-fg-muted">
              界面只做一件事：将机体上报的状态如实呈现，链路降级时直接说明。我们不会以加载状态敷衍，也不会用一句「正常」掩盖一条正在劣化的链路。
            </p>
            <CheckList
              items={[
                '遥测由浏览器直连机体，平台不转发、不存储',
                '地图上的航迹仅在本次浏览中累积，刷新即清空',
                '链路降级、丢帧与延迟汇聚于同一面板，按三级阈值标色',
                '机队总览与单机管理共用同一份权限定义',
              ]}
            />
            <TextLink to="/platform#realtime">查看链路设计</TextLink>
          </div>

          <Screenshot id="fleet-overview" sizes={SHOT_SIZES_ASIDE} />
        </div>

        <div className="mt-10">
          <Screenshot id="single-drone-realtime" sizes={SHOT_SIZES_FULL} scrollOnSmall />
        </div>
      </Section>

      <Section tone="soft" id="coming-soon">
        <SectionHeading
          eyebrow={HOME_COMING_SOON.eyebrow}
          title={HOME_COMING_SOON.title}
          lead={HOME_COMING_SOON.lead}
          actions={<TextLink to={HOME_COMING_SOON.action.to}>{HOME_COMING_SOON.action.label}</TextLink>}
        />

        <div className="mt-10 grid gap-4 md:grid-cols-2">
          {comingSoon.map((product, index) => (
            <Reveal key={product.id} delay={index} as="div">
              <ProductCard product={product} showNote className="h-full" />
            </Reveal>
          ))}
        </div>
      </Section>

      <Section id="access">
        <SectionHeading
          eyebrow="接入方式"
          title="从一台自研机体，到一整支企业机队"
          lead="平台不限定机体品牌，仅以接口为准。个人开发者按文档实现即可接入；机构可将整套平台部署至自有账号。"
        />

        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          {ACCESS_PATHS.map((path, index) => (
            <Reveal key={path.id} delay={index}>
              <Card interactive className="flex h-full flex-col gap-4">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-grad-tint text-brand">
                  <Icon name={path.icon} size={21} />
                </span>
                <span className="label-mono">{path.eyebrow}</span>
                <h3 className="text-lg font-semibold text-fg">{path.title}</h3>
                <p className="text-sm leading-relaxed text-fg-muted">{path.summary}</p>
                <ol className="mt-1 space-y-3 border-t border-line pt-4">
                  {path.steps.map((step, stepIndex) => (
                    <li key={step.title} className="flex gap-3">
                      <span className="metric-value mt-0.5 text-xs text-brand">
                        {String(stepIndex + 1).padStart(2, '0')}
                      </span>
                      <span>
                        <span className="block text-sm font-medium text-fg">{step.title}</span>
                        <span className="mt-0.5 block text-xs leading-relaxed text-fg-muted">{step.text}</span>
                      </span>
                    </li>
                  ))}
                </ol>
                <p className="mt-auto rounded-lg bg-surface-2 px-3.5 py-3 text-xs leading-relaxed text-fg-muted">
                  {path.footnote}
                </p>
              </Card>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section tone="raised" id="solutions">
        <SectionHeading
          eyebrow="适用场景"
          title="同一套平台，适配不同规模的运营"
          lead="场景之间的差异不在于功能多少，而在于由谁值守、机体数量几何、数据是否需要留存于自有账号。"
          actions={<TextLink to="/solutions">展开看每个场景</TextLink>}
        />

        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {HOME_SOLUTIONS.map((solution, index) => (
            <Reveal key={solution.id} delay={index} as="div">
              <Link to={`/solutions#${solution.id}`} className="card-surface card-hover flex h-full flex-col gap-3 p-5">
                <Icon name={solution.icon} size={19} className="text-brand" />
                <h3 className="text-sm font-semibold text-fg">{solution.title}</h3>
                <p className="text-sm leading-relaxed text-fg-muted">{solution.text}</p>
              </Link>
            </Reveal>
          ))}
          <Reveal delay={5} as="div">
            <div className="card-surface flex h-full flex-col justify-between gap-3 border-dashed p-5">
              <div>
                <Icon name="sliders" size={19} className="text-brand" />
                <h3 className="mt-3 text-sm font-semibold text-fg">还有其他场景？</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-fg-muted">
                  请将具体情况写入邮件，我们将依据贵方的运营方式判断平台需要调整的部分。
                </p>
              </div>
              <TextLink to="/contact">沟通您的应用场景</TextLink>
            </div>
          </Reveal>
        </div>
      </Section>

      <Section tone="soft" spacing="loose">
        <div className="flex flex-col items-start gap-6 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <span className="label-mono text-brand">{HOME_CTA.eyebrow}</span>
            <h2 className="mt-3 text-[1.5rem] font-semibold text-fg md:text-[2.25rem]">{HOME_CTA.title}</h2>
            <p className="mt-3 text-[0.9375rem] leading-relaxed text-fg-muted">{HOME_CTA.text}</p>
          </div>
          <ActionRow actions={HOME_CTA.actions} className="shrink-0" />
        </div>
      </Section>
    </>
  );
}
